/**
 * The TP policy's dates when they are not entered — two insurers (Reliance OD + the TP at KSHEMA / National), 2026-10-23.
 *
 * The TP policy is bought by us, now, so its dates need not be typed (the owner): START = today (India), END = the policy's
 * year selection ("No of Year", `paCoverYears`: 1 to 5, default 1) after it, the day before the anniversary — how insurers
 * print a policy period (start 23-10-2026, 1 Year = end 22-10-2027). Only a date that is EMPTY is worked out; a given one
 * stays. Mirrors RayalBrokers-backend/shared/existingTpPolicy.js (withDefaultDates) and the automation's
 * lib/existingTpGate.js (defaultTpDates): the same rule in all three, so what the form shows is what is saved and what Reliance gets.
 *
 * Days here are "YYYY-MM-DD" text (what a date box holds), never a Date: no time zone can move them.
 */

const IST_ZONE = "Asia/Kolkata";
const IST_OFFSET_MINUTES = 330; // India has no DST: +05:30 all year

const pad = (n) => String(n).padStart(2, "0");
const text = ([y, m, d]) => `${y}-${pad(m)}-${pad(d)}`;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The year selection a policy was sold with: a whole number from 1 to 5, anything else (nothing, "", 0, 7) is 1. */
export const tpYearsOf = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : 1;
};

/** "1 Year" / "5 Years" — the wording of the form's "No of Year" box. */
export const tpYearsLabel = (value) => {
  const n = tpYearsOf(value);
  return `${n} Year${n > 1 ? "s" : ""}`;
};

/** Today's calendar day in India as "YYYY-MM-DD" (the browser's own zone does not matter). `now` = epoch ms. */
export const indiaToday = (now = Date.now()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: IST_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(now));
    const pick = (type) => Number(parts.find((p) => p.type === type).value);
    const day = [pick("year"), pick("month"), pick("day")];
    if (day.every((n) => Number.isFinite(n))) return text(day);
  } catch (e) {
    // no zone data in this runtime: the fixed offset below
  }
  const shifted = new Date(new Date(now).getTime() + IST_OFFSET_MINUTES * 60000);
  return text([shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate()]);
};

/** The last day of a policy of `years` year(s) that starts on `start` ("YYYY-MM-DD"): the day before the anniversary. "" when `start` is not a day. */
export const tpEndDay = (start, years) => {
  const m = DAY.exec(String(start || ""));
  if (!m) return "";
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y + tpYearsOf(years), mo - 1, d)); // 29 Feb rolls to 1 Mar, the day before is 28 Feb
  t.setUTCDate(t.getUTCDate() - 1);
  return text([t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()]);
};

/** What the View / Preview pages say for a date nobody entered on a two-insurer policy ("start" | "end"). */
export const tpAutoText = (which, years) => (which === "start" ? "Automatic - today" : `Automatic - ${tpYearsLabel(years)} after the start`);

/**
 * The two dates with the empty ones worked out: start = today, end = `years` after the start (the given one, else today).
 * A given start that is not a day is left alone (the form's own check says so).
 *
 * @param {{startDate?:string, endDate?:string, years?:*, now?:number}} o   days as "YYYY-MM-DD" or ""
 * @returns {{startDate:string, endDate:string}}
 */
export const defaultTpDates = ({ startDate = "", endDate = "", years, now = Date.now() } = {}) => {
  let start = String(startDate || "");
  let end = String(endDate || "");
  if (start && !DAY.test(start)) return { startDate: start, endDate: end };
  if (!start) start = indiaToday(now);
  if (!end) end = tpEndDay(start, years);
  return { startDate: start, endDate: end };
};
